import { safeGet } from "./httpClient";
import { FALLBACK_LANGUAGE } from "../config/languages";
import { resolveLanguage } from "../helpers/routes";

const STRAPI_BASE_URL =
  process.env.REACT_APP_STRAPI_URL ||
  process.env.REACT_APP_STRAPI_API_URL ||
  "http://167.86.75.204:1341";

const STRAPI_API_BASE = `${STRAPI_BASE_URL.replace(/\/$/, "")}/api`;

const buildStrapiUrl = (endpoint) => {
  if (!endpoint) {
    return STRAPI_API_BASE;
  }
  if (endpoint.startsWith("http")) {
    return endpoint;
  }
  const normalized = endpoint.startsWith("/") ? endpoint : `/${endpoint}`;
  return `${STRAPI_API_BASE}${normalized}`;
};

export const strapiGet = async (endpoint, { unwrap = true } = {}) => {
  const result = await safeGet(buildStrapiUrl(endpoint));

  if (!result.ok) {
    return result;
  }

  if (!unwrap) {
    return result;
  }

  const payload = result.data;
  const unwrapped = payload?.data ?? null;

  if (unwrapped === null) {
    return {
      ok: false,
      reason: "NOT_FOUND",
      status: result.status,
      data: null,
      error: null,
    };
  }

  return {
    ...result,
    data: unwrapped,
  };
};

export const getStrapiBaseUrl = () => STRAPI_BASE_URL;

const appendLocaleParam = (endpoint, locale) => {
  if (!locale || !endpoint) {
    return endpoint;
  }

  if (endpoint.startsWith("http")) {
    const url = new URL(endpoint);
    if (!url.searchParams.has("locale")) {
      url.searchParams.set("locale", locale);
    }
    return url.toString();
  }

  if (endpoint.includes("locale=")) {
    return endpoint;
  }

  const separator = endpoint.includes("?") ? "&" : "?";
  return `${endpoint}${separator}locale=${encodeURIComponent(locale)}`;
};

export const getWithLocaleFallback = async (
  endpoint,
  { locale, fallbackLocale, unwrap = true } = {}
) => {
  const primaryLocale = resolveLanguage(locale);
  const fallback =
    fallbackLocale && fallbackLocale !== primaryLocale
      ? fallbackLocale
      : FALLBACK_LANGUAGE;

  const primaryResult = await strapiGet(appendLocaleParam(endpoint, primaryLocale), {
    unwrap,
  });

  if (primaryResult.ok || primaryResult.reason !== "NOT_FOUND" || !fallback) {
    return primaryResult;
  }

  if (fallback === primaryLocale) {
    return primaryResult;
  }

  const fallbackResult = await strapiGet(appendLocaleParam(endpoint, fallback), {
    unwrap,
  });

  if (fallbackResult.ok) {
    return {
      ...fallbackResult,
      usedFallback: true,
      fallbackLocale: fallback,
      primaryLocale,
    };
  }

  if (fallbackResult.reason === "NOT_FOUND") {
    return {
      ...fallbackResult,
      reason: "NOT_FOUND",
      data: null,
      error: null,
    };
  }

  return fallbackResult;
};
